import { CheckCircle, ClipboardList, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';

export type SessionEntry = {
  toolId: string;
  reference: string;
  ok: boolean;
  error?: string;
  timestamp: string;
};

type WorkshopSessionLogProps = {
  entries: SessionEntry[];
  onClear: () => void;
};

export function WorkshopSessionLog({ entries, onClear }: WorkshopSessionLogProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  if (entries.length === 0) return null;

  const successCount = entries.filter((e) => e.ok).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <div className="eyebrow flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5" />
          {t('traceability.workshop.toolForm.session.title')}
          <Badge tone="primary">
            {t('traceability.workshop.toolForm.session.count', { n: String(successCount) })}
          </Badge>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          {t('traceability.workshop.toolForm.session.clear')}
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        {entries.map((entry, i) => (
          <div
            key={i}
            className={cn(
              'grid grid-cols-[16px_1fr_auto_auto] items-center gap-3 rounded-lg px-3 py-2 text-xs',
              i % 2 === 0 ? 'bg-muted' : 'border border-border'
            )}
          >
            {entry.ok ? (
              <CheckCircle className="h-3.5 w-3.5 text-success" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-destructive" />
            )}
            <span className="font-mono">{entry.toolId}</span>
            {entry.ok ? (
              <span className="text-muted-foreground">{resolve(entry.reference).label}</span>
            ) : (
              <span className="text-destructive">{entry.error}</span>
            )}
            <span className="text-muted-foreground">{entry.timestamp}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
